import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SendIcon from '@mui/icons-material/Send'
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  createSupportPackPaymentLinkRequest,
  getSupportPaymentLinks,
} from 'src/supportPortal/services/paymentLinksService'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import SupportPaymentLinkSendDialog from './SupportPaymentLinkSendDialog'

const SCOPE_PRACTITIONER = 'practitioner'
const SCOPE_CLINIC_LOCATION = 'clinic-location'
const SCOPE_CLINIC_BRAND = 'clinic-brand'
const DEFAULT_PAYMENT_LINK_EXPIRY_DAYS = 15
const MIN_PAYMENT_LINK_EXPIRY_DAYS = 1
const MAX_PAYMENT_LINK_EXPIRY_DAYS = 180

function formatDateTime(value) {
  if (!value) {
    return '-'
  }
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatScopeLabel(scopeType) {
  if (scopeType === SCOPE_PRACTITIONER) {
    return 'Personal for clinic'
  }
  if (scopeType === SCOPE_CLINIC_LOCATION) {
    return 'Clinic appointment pack'
  }
  if (scopeType === SCOPE_CLINIC_BRAND) {
    return 'Clinic brand pack'
  }
  return 'Pack'
}

function buildBundleSummary(bundlePack) {
  const bundleItems = Array.isArray(bundlePack?.bundle) ? bundlePack.bundle : []
  const totalUnits = bundleItems.reduce((sum, item) => sum + Number(item?.maxCount || 0), 0)
  if (totalUnits > 0) {
    return `${totalUnits} bundled uses`
  }
  if (bundlePack?.sessionCount) {
    return `${bundlePack.sessionCount} sessions`
  }
  return `${bundleItems.length} bundled plan${bundleItems.length === 1 ? '' : 's'}`
}

function getValidityLabel(bundlePack) {
  const expiryInDays = Number(bundlePack?.expiryInDays || 0)
  if (expiryInDays > 0) {
    return `${expiryInDays} days from purchase`
  }
  return 'No expiry configured'
}

function getPackRequestStatusColor(statusLabel) {
  if (statusLabel === 'Paid') {
    return 'success'
  }
  if (statusLabel === 'Expired') {
    return 'error'
  }
  if (statusLabel === 'Pending') {
    return 'warning'
  }
  return 'default'
}

function getExpiryPreviewDate(expiryDays) {
  const days = Number(expiryDays)
  if (!Number.isFinite(days) || days < MIN_PAYMENT_LINK_EXPIRY_DAYS) {
    return null
  }
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000)
}

function getPaymentLinkExpiryText(item) {
  if (!item?.paymentLinkExpiredTime) {
    return 'Payment link expiry not available'
  }
  const expiresAt = new Date(item.paymentLinkExpiredTime)
  const expired = Number.isFinite(expiresAt.getTime()) && expiresAt.getTime() < Date.now()
  return `${expired ? 'Payment link expired' : 'Payment link expires'} ${formatDateTime(item.paymentLinkExpiredTime)}`
}

function getClinicBrandLabel(option) {
  return option?.brandName || option?.title || option?.name || option?.label || option?.id || 'Clinic brand'
}

function getClinicLocationLabel(option) {
  return option?.title || option?.name || option?.label || option?.id || 'Clinic location'
}

function getPractitionerLabel(option) {
  return option?.name || option?.fullName || option?.label || option?.id || 'Practitioner'
}

export default function SupportPatientPackRequestsPanel({
  patient,
  practitionerId,
  clinicBrandId,
  clinicLocationId,
  clinicBrandOptions = [],
  clinicLocationOptions = [],
  practitionerOptions = [],
  allowScopeSelection = false,
  scopeOptionsLoading = false,
  title = 'Pack Purchase Requests',
}) {
  const [loadingRequests, setLoadingRequests] = useState(false)
  const [loadingBundleOptions, setLoadingBundleOptions] = useState(false)
  const [requestItems, setRequestItems] = useState([])
  const [bundlePackOptions, setBundlePackOptions] = useState([])
  const [bundlePackOptionsError, setBundlePackOptionsError] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedClinicBrandId, setSelectedClinicBrandId] = useState('')
  const [selectedClinicLocationId, setSelectedClinicLocationId] = useState('')
  const [selectedPractitionerId, setSelectedPractitionerId] = useState('')
  const [selectedBundlePackId, setSelectedBundlePackId] = useState('')
  const [paymentLinkExpiryDays, setPaymentLinkExpiryDays] = useState(DEFAULT_PAYMENT_LINK_EXPIRY_DAYS)
  const [creatingRequest, setCreatingRequest] = useState(false)
  const [sendDialogRow, setSendDialogRow] = useState(null)

  const effectivePractitionerId = practitionerId || patient?.mentorId || ''
  const effectiveClinicBrandId =
    clinicBrandId ||
    patient?.clinicBrandId ||
    patient?.brandData?.id ||
    patient?.brandData?.clinicBrandId ||
    ''
  const effectiveClinicLocationId =
    clinicLocationId ||
    patient?.clinicId ||
    patient?.clinicData?.id ||
    ''
  const patientId = patient?.id
  const languageCode = patient?.defaultCommunicationLanguage || 'en'
  const countryCode =
    patient?.location?.countryCode ||
    patient?.countryCode ||
    patient?.brandData?.countryCode ||
    'IN'
  const availableClinicBrands = useMemo(() => {
    if (Array.isArray(clinicBrandOptions) && clinicBrandOptions.length > 0) {
      return clinicBrandOptions
    }
    if (!effectiveClinicBrandId) {
      return []
    }
    return [
      {
        id: effectiveClinicBrandId,
        title: patient?.brandData?.brandName || patient?.brandData?.title || 'Selected clinic brand',
      },
    ]
  }, [clinicBrandOptions, effectiveClinicBrandId, patient?.brandData?.brandName, patient?.brandData?.title])
  const availableClinicLocations = useMemo(() => {
    const baseOptions =
      Array.isArray(clinicLocationOptions) && clinicLocationOptions.length > 0
        ? clinicLocationOptions
        : effectiveClinicLocationId
          ? [
              {
                id: effectiveClinicLocationId,
                title: patient?.clinicData?.title || 'Selected clinic location',
                clinicBrandId: effectiveClinicBrandId || undefined,
              },
            ]
          : []
    const activeBrandId = allowScopeSelection ? selectedClinicBrandId : effectiveClinicBrandId
    if (!activeBrandId) {
      return baseOptions
    }
    return baseOptions.filter((option) => !option?.clinicBrandId || option?.clinicBrandId === activeBrandId)
  }, [
    allowScopeSelection,
    clinicLocationOptions,
    effectiveClinicBrandId,
    effectiveClinicLocationId,
    patient?.clinicData?.title,
    selectedClinicBrandId,
  ])
  const availablePractitioners = useMemo(() => {
    if (Array.isArray(practitionerOptions) && practitionerOptions.length > 0) {
      return practitionerOptions
    }
    if (!effectivePractitionerId) {
      return []
    }
    return [
      {
        id: effectivePractitionerId,
        name: patient?.mentorName || 'Selected practitioner',
      },
    ]
  }, [effectivePractitionerId, patient?.mentorName, practitionerOptions])
  const activeClinicBrandId = allowScopeSelection ? selectedClinicBrandId : effectiveClinicBrandId
  const activeClinicLocationId = allowScopeSelection ? selectedClinicLocationId : effectiveClinicLocationId
  const activePractitionerId = allowScopeSelection ? selectedPractitionerId : effectivePractitionerId

  const selectedBundlePack = useMemo(
    () => bundlePackOptions.find((item) => item?.id === selectedBundlePackId) || null,
    [bundlePackOptions, selectedBundlePackId],
  )
  const normalizedPaymentLinkExpiryDays = Number(paymentLinkExpiryDays)
  const isPaymentLinkExpiryValid =
    Number.isInteger(normalizedPaymentLinkExpiryDays) &&
    normalizedPaymentLinkExpiryDays >= MIN_PAYMENT_LINK_EXPIRY_DAYS &&
    normalizedPaymentLinkExpiryDays <= MAX_PAYMENT_LINK_EXPIRY_DAYS
  const paymentLinkExpiryPreviewDate = useMemo(
    () => getExpiryPreviewDate(normalizedPaymentLinkExpiryDays),
    [normalizedPaymentLinkExpiryDays],
  )
  const createButtonDisabledReason = useMemo(() => {
    if (!patientId) {
      return 'This patient record is missing a patient ID, so a pack purchase request cannot be created yet.'
    }
    if (scopeOptionsLoading) {
      return 'Clinic and practitioner options are still loading for this patient.'
    }
    if (!effectiveClinicBrandId && availableClinicBrands.length === 0) {
      return 'No clinic brands are available yet to create a pack purchase request for this patient.'
    }
    return ''
  }, [
    effectiveClinicBrandId,
    availableClinicBrands.length,
    patientId,
    scopeOptionsLoading,
  ])
  const isCreateButtonDisabled = Boolean(createButtonDisabledReason)

  const loadPackRequests = useCallback(async () => {
    if (!patientId) {
      setRequestItems([])
      return
    }
    setLoadingRequests(true)
    const result = await getSupportPaymentLinks({
      limit: 100,
      'patient-id': patientId,
      category: 'packs',
    })
    if (result?.ok) {
      setRequestItems(result.items || [])
    } else {
      setRequestItems([])
    }
    setLoadingRequests(false)
  }, [patientId])

  const loadBundlePackOptions = useCallback(async () => {
    setBundlePackOptionsError('')
    setSelectedBundlePackId('')
    if (!activeClinicBrandId) {
      setBundlePackOptions([])
      return
    }

    const requests = []
    if (activePractitionerId && activeClinicLocationId) {
      requests.push({
        scopeType: SCOPE_PRACTITIONER,
        label: formatScopeLabel(SCOPE_PRACTITIONER),
        params: {
          scopeType: SCOPE_PRACTITIONER,
          'mentor-id': activePractitionerId,
          'clinic-brand-id': activeClinicBrandId,
          'clinic-location-id': activeClinicLocationId,
        },
      })
    }
    if (activeClinicLocationId) {
      requests.push({
        scopeType: SCOPE_CLINIC_LOCATION,
        label: formatScopeLabel(SCOPE_CLINIC_LOCATION),
        params: {
          scopeType: SCOPE_CLINIC_LOCATION,
          'clinic-brand-id': activeClinicBrandId,
          'clinic-location-id': activeClinicLocationId,
        },
      })
    }
    requests.push({
      scopeType: SCOPE_CLINIC_BRAND,
      label: formatScopeLabel(SCOPE_CLINIC_BRAND),
      params: {
        scopeType: SCOPE_CLINIC_BRAND,
        'clinic-brand-id': activeClinicBrandId,
      },
    })

    setLoadingBundleOptions(true)
    try {
      const responses = await Promise.all(
        requests.map(async (request) => {
          const response = await getSPDataWithParams('/api/secure/cc/support/bundle-pack/list', request.params)
          return { request, response }
        }),
      )

      const uniqueOptions = []
      responses.forEach(({ request, response }) => {
        if (!isCommonResponseSuccessfulV2(response)) {
          return
        }
        const bundlePacks = getDataMapFromCommonResponse(response)?.['plan-bundle-pack'] || []
        bundlePacks
          .filter((bundlePack) => bundlePack?.enabled !== false)
          .forEach((bundlePack) => {
            if (uniqueOptions.some((existing) => existing.id === bundlePack?.id)) {
              return
            }
            uniqueOptions.push({
              ...bundlePack,
              scopeType: request.scopeType,
              scopeLabel: request.label,
            })
          })
      })
      setBundlePackOptions(uniqueOptions)
    } catch (error) {
      console.error('Error loading bundle pack options:', error)
      setBundlePackOptions([])
      setBundlePackOptionsError('Eligible pack plans could not be loaded right now. Please refresh and try again.')
    } finally {
      setLoadingBundleOptions(false)
    }
  }, [activeClinicBrandId, activeClinicLocationId, activePractitionerId])

  useEffect(() => {
    loadPackRequests()
  }, [loadPackRequests])

  useEffect(() => {
    if (!createDialogOpen) {
      return
    }
    loadBundlePackOptions()
  }, [createDialogOpen, loadBundlePackOptions])

  useEffect(() => {
    if (!createDialogOpen || !allowScopeSelection) {
      return
    }
    if (selectedClinicLocationId
      && !availableClinicLocations.some((option) => option?.id === selectedClinicLocationId)) {
      setSelectedClinicLocationId('')
    }
  }, [allowScopeSelection, availableClinicLocations, createDialogOpen, selectedClinicLocationId])

  const handleCopy = async (link) => {
    if (!link) {
      return
    }
    await navigator.clipboard.writeText(link)
    toast.success('Payment link copied')
  }

  const createRequest = async (openSendDialogAfterCreate = false) => {
    if (!patientId || !selectedBundlePackId) {
      return
    }
    if (!isPaymentLinkExpiryValid) {
      toast.error(`Choose a payment link expiry between ${MIN_PAYMENT_LINK_EXPIRY_DAYS} and ${MAX_PAYMENT_LINK_EXPIRY_DAYS} days`)
      return
    }
    setCreatingRequest(true)
    const result = await createSupportPackPaymentLinkRequest({
      patientId,
      bundlePackId: selectedBundlePackId,
      languageCode,
      countryCode,
      clientTransactionId: uuidv4(),
      paymentLinkExpiryInDays: normalizedPaymentLinkExpiryDays,
    })
    setCreatingRequest(false)

    if (!result?.ok || !result?.item) {
      toast.error(result?.message || 'Could not create pack purchase request')
      return
    }

    toast.success('Pack purchase request created')
    setCreateDialogOpen(false)
    setSelectedBundlePackId('')
    await loadPackRequests()
    if (openSendDialogAfterCreate) {
      setSendDialogRow(result.item)
    }
  }

  const handleOpenCreateDialog = () => {
    setSelectedClinicBrandId(effectiveClinicBrandId || (availableClinicBrands.length === 1 ? availableClinicBrands[0]?.id : ''))
    setSelectedClinicLocationId(effectiveClinicLocationId || '')
    setSelectedPractitionerId(effectivePractitionerId || '')
    setSelectedBundlePackId('')
    setPaymentLinkExpiryDays(DEFAULT_PAYMENT_LINK_EXPIRY_DAYS)
    setBundlePackOptions([])
    setBundlePackOptionsError('')
    setCreateDialogOpen(true)
  }

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false)
    setSelectedBundlePackId('')
    setPaymentLinkExpiryDays(DEFAULT_PAYMENT_LINK_EXPIRY_DAYS)
  }

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2.5,
          borderColor: alpha('#2563eb', 0.18),
          background: 'linear-gradient(135deg, rgba(37,99,235,0.05) 0%, rgba(255,255,255,0.98) 100%)',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create a pack purchase request for this patient. Requests also appear in Payment Links under the Packs tab.
              </Typography>
              {isCreateButtonDisabled ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#92400e' }}>
                  {createButtonDisabledReason}
                </Typography>
              ) : null}
            </Box>
            <Tooltip
              title={createButtonDisabledReason}
              placement="top"
              disableHoverListener={!isCreateButtonDisabled}
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleOpenCreateDialog}
                  disabled={isCreateButtonDisabled}
                >
                  Create Pack Request
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {loadingRequests ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : requestItems.length > 0 ? (
            <Stack spacing={1.25}>
              {requestItems.map((item) => (
                <Paper key={item?.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item?.itemTitle || item?.bundlePackTitle || 'Pack request'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created {formatDateTime(item?.paymentLinkCreatedTime || item?.createdTime)}
                        </Typography>
                        {item?.itemSummary || item?.itemSubtitle ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {item.itemSummary || item.itemSubtitle}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          color={getPackRequestStatusColor(item?.statusLabel)}
                          label={item?.statusLabel || 'Unknown'}
                        />
                        {item?.bundlePackTitle ? (
                          <Chip size="small" variant="outlined" label={item.bundlePackTitle} />
                        ) : null}
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {item?.practitionerName ? (
                        <Chip size="small" variant="outlined" label={`Practitioner: ${item.practitionerName}`} />
                      ) : null}
                      {item?.clinicBrandName ? (
                        <Chip size="small" variant="outlined" label={`Brand: ${item.clinicBrandName}`} />
                      ) : null}
                      {item?.clinicLocationName ? (
                        <Chip size="small" variant="outlined" label={`Location: ${item.clinicLocationName}`} />
                      ) : null}
                      {typeof item?.totalUnits === 'number' ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${item.remainingUnits ?? item.totalUnits}/${item.totalUnits} uses remaining`}
                        />
                      ) : null}
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                      <Stack spacing={0.35}>
                        <Typography variant="body2" color="text.secondary">
                          {getPriceStringWithCurrency(item?.amount, item?.currency, true)}
                        </Typography>
                        {item?.paymentLinkExpiredTime ? (
                          <Typography variant="caption" color="text.secondary">
                            {getPaymentLinkExpiryText(item)}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {item?.paymentLink ? (
                          <>
                            <Button
                              size="small"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => handleCopy(item.paymentLink)}
                            >
                              Copy
                            </Button>
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={() => window.open(item.paymentLink, '_blank', 'noopener,noreferrer')}
                            >
                              Open
                            </Button>
                          </>
                        ) : null}
                        <Button
                          size="small"
                          startIcon={<SendIcon />}
                          disabled={item?.canSend !== true}
                          onClick={() => setSendDialogRow(item)}
                        >
                          {item?.canSend ? 'Send' : 'View Only'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No pack purchase requests have been created for this patient yet.</Alert>
          )}
        </Stack>
      </Paper>

      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Pack Purchase Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {allowScopeSelection ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Choose the clinic scope first. Brand packs use only the clinic brand, clinic packs use brand plus clinic location, and personal packs also use the practitioner.
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="support-pack-request-brand-label">Clinic Brand</InputLabel>
                  <Select
                    labelId="support-pack-request-brand-label"
                    label="Clinic Brand"
                    value={selectedClinicBrandId}
                    onChange={(event) => {
                      setSelectedClinicBrandId(event.target.value)
                      setSelectedClinicLocationId('')
                      setSelectedBundlePackId('')
                    }}
                  >
                    <MenuItem value="">
                      <em>Select clinic brand</em>
                    </MenuItem>
                    {availableClinicBrands.map((brand) => (
                      <MenuItem key={brand?.id} value={brand?.id}>
                        {getClinicBrandLabel(brand)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" disabled={!selectedClinicBrandId}>
                  <InputLabel id="support-pack-request-location-label">Clinic Location</InputLabel>
                  <Select
                    labelId="support-pack-request-location-label"
                    label="Clinic Location"
                    value={selectedClinicLocationId}
                    onChange={(event) => {
                      setSelectedClinicLocationId(event.target.value)
                      setSelectedBundlePackId('')
                    }}
                  >
                    <MenuItem value="">
                      <em>Brand only / all locations</em>
                    </MenuItem>
                    {availableClinicLocations.map((location) => (
                      <MenuItem key={location?.id} value={location?.id}>
                        {getClinicLocationLabel(location)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="support-pack-request-practitioner-label">Practitioner</InputLabel>
                  <Select
                    labelId="support-pack-request-practitioner-label"
                    label="Practitioner"
                    value={selectedPractitionerId}
                    onChange={(event) => {
                      setSelectedPractitionerId(event.target.value)
                      setSelectedBundlePackId('')
                    }}
                  >
                    <MenuItem value="">
                      <em>No practitioner filter</em>
                    </MenuItem>
                    {availablePractitioners.map((practitioner) => (
                      <MenuItem key={practitioner?.id} value={practitioner?.id}>
                        {getPractitionerLabel(practitioner)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : null}

            {!activeClinicBrandId ? (
              <Alert severity="info">
                Choose a clinic brand to load eligible pack plans.
              </Alert>
            ) : null}

            {activePractitionerId && !activeClinicLocationId ? (
              <Alert severity="info">
                Choose a clinic location as well if you want to include personal practitioner packs. Brand-level packs will still load.
              </Alert>
            ) : null}

            {bundlePackOptionsError ? (
              <Alert severity="error">{bundlePackOptionsError}</Alert>
            ) : null}

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Payment Link Expiry"
              value={paymentLinkExpiryDays}
              onChange={(event) => setPaymentLinkExpiryDays(event.target.value)}
              inputProps={{
                min: MIN_PAYMENT_LINK_EXPIRY_DAYS,
                max: MAX_PAYMENT_LINK_EXPIRY_DAYS,
                step: 1,
              }}
              error={!isPaymentLinkExpiryValid}
              helperText={
                isPaymentLinkExpiryValid && paymentLinkExpiryPreviewDate
                  ? `Payment link will expire on ${formatDateTime(paymentLinkExpiryPreviewDate)}.`
                  : `Enter ${MIN_PAYMENT_LINK_EXPIRY_DAYS}-${MAX_PAYMENT_LINK_EXPIRY_DAYS} days.`
              }
            />

            {loadingBundleOptions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !activeClinicBrandId ? null : bundlePackOptions.length === 0 ? (
              <Alert severity="info">
                No eligible bundle packs were found for the selected clinic scope yet.
              </Alert>
            ) : (
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="support-pack-request-select-label">Bundle Pack</InputLabel>
                  <Select
                    labelId="support-pack-request-select-label"
                    label="Bundle Pack"
                    value={selectedBundlePackId}
                    onChange={(event) => setSelectedBundlePackId(event.target.value)}
                  >
                    {bundlePackOptions.map((bundlePack) => (
                      <MenuItem key={bundlePack?.id} value={bundlePack?.id}>
                        {bundlePack?.title} • {bundlePack?.scopeLabel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedBundlePack ? (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />} label={selectedBundlePack.scopeLabel} />
                        <Chip size="small" variant="outlined" label={buildBundleSummary(selectedBundlePack)} />
                      </Stack>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {selectedBundlePack?.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getPriceStringWithCurrency(
                          selectedBundlePack?.finalPrice ?? selectedBundlePack?.price,
                          selectedBundlePack?.currency,
                          true,
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getValidityLabel(selectedBundlePack)}
                      </Typography>
                      {paymentLinkExpiryPreviewDate ? (
                        <Typography variant="caption" color="text.secondary">
                          Payment link expiry: {formatDateTime(paymentLinkExpiryPreviewDate)}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            onClick={() => createRequest(false)}
            disabled={!selectedBundlePackId || !isPaymentLinkExpiryValid || creatingRequest}
          >
            {creatingRequest ? 'Creating...' : 'Create Request'}
          </Button>
          <Button
            variant="contained"
            onClick={() => createRequest(true)}
            disabled={!selectedBundlePackId || !isPaymentLinkExpiryValid || creatingRequest}
          >
            {creatingRequest ? 'Creating...' : 'Create & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <SupportPaymentLinkSendDialog
        open={!!sendDialogRow}
        row={sendDialogRow}
        onClose={() => setSendDialogRow(null)}
        onSent={() => {
          setSendDialogRow(null)
          loadPackRequests()
        }}
      />
    </>
  )
}
